<template>
  <component-wrapper class="newsroom-group-page-fi">
    <grid-container-fi
      class="newsroom-group-page-fi__grid-container"
      :container="false"
    >
      <div class="newsroom-group-page-fi__headlines">
        <headline-fi
          v-if="fields.headline?.value"
          class="newsroom-group-page-fi__headline"
          tag="h1"
          :size="60"
        >
          {{ fields.headline.value }}
        </headline-fi>
      </div>
      <div class="newsroom-group-page-fi__all-news">
        <loading-indicator
          v-if="isLoading"
          variant="spinnerLocal"
          class="spinner-local--rebrush newsroom-group-page-fi__loading-indicator"
        />

        <teaser-big-image-element-fi
          v-for="(news, index) in categoryPressNews"
          :key="news.category"
          :image="news.image ? { src: news.image } : null"
          :image-pos-right="index % 2 === 0"
          :bg-color="index % 2 === 0 ? 'white' : 'light'"
        >
          <template #content>
            <span class="badge-fi">
              {{ news.date }}
            </span>
            <h3 v-if="news.headline">
              {{ news.headline }}
            </h3>
            <p v-if="news.paragraph">
              {{ news.paragraph }}
            </p>
            <a
              v-if="news.link && teaserButtonText?.length > 0"
              :href="news.link"
              class="button-fi__tertiary"
            >
              {{ teaserButtonText }}
            </a>
          </template>
        </teaser-big-image-element-fi>

        <section-divider-fi />

        <pagination-progress-bar
          :pagination-page="page + 1"
          :pagination-total="newsCount"
          :progressbar-value="pagingPercentage"
          :disabled-state="isLoading"
          :show-more-button="categoryPressNews.length < newsCount"
          :query-params="null"
          @loadmore="fetchMore"
        >
          <template v-if="(page + 1) * pageSize <= newsCount">
            <span>{{
              $dict.get('product-List.Pagination.Show-Sum', [(page + 1) * pageSize, newsCount])
            }}</span>
          </template>

          <template v-else>
            <span>{{ $dict.get('product-List.Pagination.Show-Sum', [newsCount, newsCount]) }}</span>
          </template>
        </pagination-progress-bar>

        <section-divider-fi />
      </div>
    </grid-container-fi>
  </component-wrapper>
</template>

<script setup lang="ts">
import { HeadlineFi, LoadingIndicator, PaginationProgressBar } from 'atoms';
import { computed, inject, onMounted, onServerPrefetch, ref, useSSRContext } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import TeaserBigImageElementFi from '@/components/teaser/teaser-big-image-element-fi/TeaserBigImageElementFi.vue';
import { useFields } from '@/composables/fields';
import { useDict, useFischer } from '@/composables/fischerPlugin';
import SectionDividerFi from '@/pages/section-divider-fi/SectionDividerFi.vue';
import { useNavigationStore } from '@/store/pinia/navigation';

defineOptions({ name: 'NewsroomGroupPageFi' });

const fischer = useFischer();
const dict = useDict();
const navigationStore = useNavigationStore();
const fields = useFields('NewsroomGroupPageFiFields');
const prefetch = inject<any>('prefetch', undefined);

const pageSize = 5;

const isLoading = ref(true);
const newsCount = ref(0);
const page = ref(0);
const categoryPressNews = ref<any[]>([]);

let ssrPrefetch: any;
let ctx: Record<string, any> = {};

const apiUrl = computed(() =>
  IS_SHOWROOM
    ? '/__mocks__/newsroom/newsroom-category.json'
    : `${fischer.hostUrlBase()}/api/press/get?lang=${fischer.siteLanguage()}&category=${fischer.pageId()}&pagesize=${pageSize}&page=${page.value}`,
);

const pagingPercentage = computed(() => (100 * categoryPressNews.value.length) / newsCount.value);

const teaserButtonText = computed(() => dict.get('newsroom.Group-Page.Teaser-Button-Text'));

function getUrlPageParam() {
  const pageParam = fischer.query('page');
  if (pageParam) {
    page.value = Number.parseInt(pageParam, 10) - 1;
  }
}

function fetchData() {
  return fetch(apiUrl.value).then((response) => {
    if (!response.ok) {
      throw new Error(`Failed to fetch press releases: ${response.status}`);
    }
    return response.json();
  });
}

// NOTE: do not convert to async, so rehydration can be done without scheduling a task
function fetchPressReleases(isFetchMore: boolean): any {
  if (!IS_SSR && !isFetchMore && prefetch) {
    return { then: (callback: (data: any) => any) => callback(prefetch) };
  }
  if (IS_SSR) {
    return { then: (callback: (data: any) => any) => callback(ssrPrefetch || {}) };
  }
  return fetchData().then((data) => ({ then: (callback: (data: any) => any) => callback(data) }));
}

function getCategoryPressNews(isFetchMore = false) {
  isLoading.value = true;

  fetchPressReleases(isFetchMore).then((pressResult: any) => {
    const getLocalDate = (date: string) =>
      date
        ? new Date(date).toLocaleDateString(fischer.siteLanguage(), {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
        : '';

    const news = pressResult?.Data?.PressReleasesList?.map((n: any) => ({
      headline: n.Document.TeaserHeadline,
      date: getLocalDate(n.Document.Date),
      paragraph: n.Document.TeaserText,
      image: n.Document.TeaserImage,
      link: n.Document.ItemLink,
      lang: n.Document.Language,
      category: n.Document.Category,
    }));

    newsCount.value = pressResult?.Data?.TotalSearchResults || 0;

    if (isFetchMore) {
      categoryPressNews.value = [...categoryPressNews.value, ...(news || [])];
    } else {
      categoryPressNews.value = news || [];
    }

    isLoading.value = false;
  });
}

function fetchMore() {
  if (fischer.query('page')) {
    window.location.search = String(window.location.search).replace(
      /\bpage=\d+/,
      `page=${page.value + 1}`,
    );
    return;
  }
  page.value += 1;
  return getCategoryPressNews(true);
}

// created
if (IS_SSR) {
  ctx = useSSRContext() || {};
} else {
  getCategoryPressNews();
}

onServerPrefetch(async () => {
  getUrlPageParam();
  ssrPrefetch = await fetchData();
  if (ssrPrefetch) {
    await getCategoryPressNews();
    ctx.prefetch = { Data: ssrPrefetch?.Data };
  }
});

onMounted(() => {
  navigationStore.setBackLink({
    text: dict.get('my-Account.Address-Overview.Back-To-Dashboard'),
    url: fischer.pages.myAccount().url,
  });
  getUrlPageParam();
});
</script>

<style src="./newsroom-group-page-fi-unscoped" />

<style scoped src="./newsroom-group-page-fi" />
